import { Table, Column, Select, Panel, Search, Tab, Tabs, Tooltip } from "@appkit4/react-components";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getDate, getTextColour, searchFilter, selectFilter } from "services/common";
import { AnalysisStatus, ListType, Profile, Role, RoleListData, RuleTest, Trend } from "routes/analysis/types/analysis";
import { TestData } from "../types/user";
import Loader from "components/common/loader";
import PercentageBar from "components/common/percentageBar";
import { UserCount } from "components/common/userStatistics";
import { SelectValue } from "@appkit4/react-components/esm/combobox/Combobox";
import { FetchAnalysisData, FetchAnalysisList, FetchTestDetails } from "routes/analysis/queries/analysis";
import { Props as LegendProps } from "recharts/types/component/Legend";
import { ActiveFilters } from "components/layout/filters/filters";
import { LoaderWrapper } from "components/common/helpers";
import ToolTip from "components/common/tooltip";
import UserMatches from "../user/userMatches";
import UsersInline from "../users/usersInline";

export const getRechartsLegend = (props: LegendProps) => {
  const { payload } = props;
  if (!payload) return null;
  return (
    <ul className="ap-legend">
      {payload.map((entry, index) => (
        <li key={`item-${index}`} className="ap-legend-item">
          <span className="ap-legend-icon" style={{ backgroundColor: entry.color }} />
          <span className="ap-legend-label">{entry.value}</span>
        </li>
      ))}
    </ul>
  );
}

export const Floater: FC<{ children: React.ReactNode, offset?: number }> = ({ children, offset = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [float, setFloat] = useState(false);
  const [width, setWidth] = useState<number>(useMemo(() => {
    const el = document.getElementById("dataview");
    return el ? el.getBoundingClientRect().width : 1420;
  }, []));

  useEffect(() => {
    const updateWidth = () => {
      const el = document.getElementById("dataview");
      if (el) {
        setWidth(el.getBoundingClientRect().width);
      }
    };

    updateWidth();

    let resizeObserver: ResizeObserver | null = null;
    const el = document.getElementById("dataview");
    if (el && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateWidth();
      });
      resizeObserver.observe(el);
    } else {
      window.addEventListener("resize", updateWidth);
    }

    return () => {
      if (resizeObserver && el) {
        resizeObserver.unobserve(el);
      } else {
        window.removeEventListener("resize", updateWidth);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      let floatLimit = ref.current ? ref.current.getBoundingClientRect().y : offset;
      if (floatLimit <= offset && !float)
        setFloat(true);
      else if (floatLimit > offset && float)
        setFloat(false);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [float, offset, width]);
  return (
    <div ref={ref}>
      {children}
      {float && <div style={{ width: width - 32, top: offset, zIndex: offset > 0 ? 999 : 1000 }} className="ap-floater">
        {children}
      </div>}
    </div>
  )
}

export const TrendIndicator: FC<{ trend: number, value?: string | (() => React.ReactNode) }> = ({ trend, value }) => (
  <Tooltip
    trigger="hover"
    position="bottom"
    distance={4}
    appendAfterTarget={true}
    content={value ? value : trend === Trend.UP ? "The number of risks has increased" : trend === Trend.DOWN ? "The number of risks has decreased" : "The number of risks has not changed"}
  >
    {
      trend === Trend.UP
        ? <span className="Appkit4-icon icon-uptrend-fill ap-font-12 ap-trend-up" aria-hidden="true"></span>
        : trend === Trend.DOWN
          ? <span className="Appkit4-icon icon-downtrend-fill ap-font-12 ap-trend-down" aria-hidden="true"></span>
          : <span className="Appkit4-icon icon-minus-outline ap-font-12" aria-hidden="true"></span>
    }
  </Tooltip>
)

export const CompositeIcon: FC<{ isComposite?: boolean, type: "role" | "profile" }> = ({ isComposite, type }) => (
  <Tooltip
    trigger="hover"
    position="bottom"
    distance={4}
    appendAfterTarget={true}
    content={isComposite ? `composite ${type}` : `${type}`}
    style={{ textTransform: "capitalize" }}
  >
    {
      type === "role"
        ? isComposite
          ? <span className="Appkit4-icon icon-venn-ab-horizontal-outline" />
          : <span className="Appkit4-icon icon-circle-empty-outline" />
        : isComposite
          ? <span className="Appkit4-icon icon-group-outline" />
          : <span className="Appkit4-icon icon-placeholder-outline ap-font-16" />
    }
  </Tooltip>
)

export const TestIcon: FC<{ type: "SA" | "SoD" }> = ({ type }) => (
  <div style={{ textAlign: "center", width: 36, display: "inline-block" }}>
    {
      type === "SoD"
        ? <p>SoD</p>
        : <p>SA</p>
    }
  </div>
)

export const Chevron: FC<{ open: Boolean, onClick?: () => void }> = ({ open, onClick }) => {
  return (<span
    className={open ? "Appkit4-icon icon-down-chevron-outline pointer" : "Appkit4-icon icon-right-chevron-outline pointer"}
    onClick={() => onClick && onClick()}
  />)
}

export const RoleItem: FC<{ role: Role, open?: Boolean, onClick?: () => void }> = ({ role, open, onClick }) => {
  return (
    <div
      className={`flex items-center p-4 gap-4 ${open ? "ap-accordion-open" : ""}`}
    >
      <div className="basis-12">
        {
          (role.hasMatches) && open !== undefined && <Chevron open={open} onClick={onClick} />
        }
      </div>
      <div className="basis-80">
        <b>{role.name}</b>
        <p>{role.description}</p>
      </div>
      <div className="basis-32">
        <CompositeIcon isComposite={role.isComposite} type="role" />
      </div>
      <div className="basis-28">
        {role.hasMatches
          ? <ToolTip
            position="bottom"
            content="Role has matches"
          >
            <span className="Appkit4-icon icon-alert-fill access-risk"></span>
          </ToolTip>
          : ""
        }
      </div>
      {(role.validFrom && role.validTo) && (
        <div className="basis-96">
          <p>{getDate(role.validFrom)} - {getDate(role.validTo)}</p>
        </div>
      )}
      {
        role.userCount && role.userTotal
          ? <div className="basis-52"><PercentageBar percentage={Math.ceil((role.userCount / role.userTotal) * 100)} dark /></div>
          : null
      }
      {
        role.userCount
          ? <div><ToolTip content="Number of users with the role (may contain duplicates)." ><span className="Appkit4-icon icon-audience-outline"></span><UserCount>{role.userCount.toLocaleString()}</UserCount></ToolTip></div>
          : null
      }
    </div>
  )
}

export const ProfileItem: FC<{ profile: Profile, open: Boolean, onClick?: () => void }> = ({ profile, open, onClick }) => (
  <div
    className={`${open ? "ap-accordion-open" : ""} flex items-center p-4 gap-4`}
  >
    <div className="basis-12">
      {
        open !== undefined && <Chevron open={open} onClick={onClick} />
      }
    </div>
    <div className="basis-80">
      {profile.name}
    </div>
    <div className="basis-32">
      <CompositeIcon isComposite={profile.isComposite} type="profile" />
    </div>
    <div className="basis-28">
      {profile.hasMatches &&
        <ToolTip
          position="right"
          content="Profile has matches"
        >
          <span className="Appkit4-icon icon-alert-fill access-risk"></span>
        </ToolTip>
      }
    </div>
    <div className={profile.compositeProfileId ? "basis-64" : "grow"}>
      {
        profile.userCount
          ? <ToolTip content="Number of users with the role (may contain duplicates)." ><span className="Appkit4-icon icon-audience-outline"></span><UserCount>{profile.userCount.toLocaleString()}</UserCount></ToolTip>
          : null
      }
    </div>
    {
      profile.compositeProfileId && <div className="grow">
        {profile.compositeProfileName}
      </div>
    }
  </div>
)

export const TestInfo: FC<{ test: TestData, open: boolean, onClick?: () => void }> = ({ test, open, onClick }) => (
  <>
    <div
      key={test.testId}
      className="ap-accordion-open flex items-center p-4 gap-4"
    >
      <div className="basis-12">
        <span
          onClick={onClick}
          className={open ? "Appkit4-icon icon-down-chevron-outline pointer" : "Appkit4-icon icon-right-chevron-outline pointer"}
        />
      </div>
      <div className="basis-96">
        <b>{test.identifier}</b>
        <p>{test.functions?.map(m => <span className="ap-function-description" key={test.testId + m.functionId}>{m.description}</span>)}</p>
      </div>
      <div className="basis-20"><TestIcon type={test.type || "SA"} /></div>
      <div className="grow">
        {test.functions?.map(f => (
          <div key={`${test.identifier}-${f.identifier}`}>
            <b>{f.identifier}</b>
          </div>
        ))}
      </div>
    </div>
    {open && <div className="ap-accordion-open p-4 pl-20">
      <p>{test.description}</p>
    </div>}
  </>
)

export const customTooltip = ({ active, payload, label }: { active?: boolean, payload?: any[], label?: string }) => {
  if (active && payload && payload.length) {
    let fill = payload[0].payload.fill;
    let textColor = getTextColour(fill);
    return (
      <div className="p-2 ap-recharts-tooltip text-nowrap" style={{ backgroundColor: fill }}>
        <p style={{ color: textColor }}>{payload[0].name}: <strong>{payload[0].value?.toLocaleString()}</strong></p>
      </div>
    )
  }
}

export const RoleInfo: FC<{ role: Role }> = ({ role }) => (
  <>
    <h1>Role</h1>
    <div
      className="flex items-center p-4 gap-4"
      key={role.roleId}
    >
      <div className="basis-96">
        <b>{role.name}</b>
        <p>{role.description}</p>
      </div>
      <div className="basis-20">
        <CompositeIcon isComposite={role.isComposite} type="role" />
      </div>
      <div className="grow">
        {role.profiles?.map(profile => (
          <p key={profile.profileId}>{profile.name}</p>
        ))}
      </div>
    </div>
  </>
)

export const AnalysisNavigator: FC = () => {
  const location = useLocation();
  const { analysisId } = useParams();

  const { data: analysisData } = FetchAnalysisData(analysisId);

  const { isPending, data, error } = FetchAnalysisList(analysisId);

  const getActiveIndex = data ? data.findIndex(w => w.analysisId === analysisId) : 0;
  return <LoaderWrapper loading={[isPending]} errors={[error]} inline>
    <div className="pb-4">
      <div className="ap-analysis-nav">
        <h2>{analysisData?.client?.name}</h2>
        <div className="flex gap-4">
          <div className="basis-1/4">
            <Select
              data={data?.map(m => ({ value: m.analysisId, label: getDate(m.extractionDate || "") }))}
              placeholder="Extraction date"
              value={analysisId}
              dropdownRenderMode="portal"
              onSelect={(value) => {
                if (!analysisId) return;
                window.location.reload();
                window.location.assign(location.pathname.replace(analysisId, value.toString()));
              }} />
          </div>
          <div className="grow">
            <p className="ap-font-medium">SAP system</p>
            {data && data[getActiveIndex]?.sapSystem && <p className="text-nowrap">{data[getActiveIndex].sapSystem?.sapSystemName || ""} <span className="Appkit4-icon icon-arrow-right-small-outline" /> {data[getActiveIndex].sapSystem?.sapNickname || ""} <span className="Appkit4-icon icon-arrow-right-small-outline" /> {data[getActiveIndex].sapSystem?.sapClient || ""}</p>}
          </div>
          <div className="basis-1/4">
            <p className="ap-font-medium">Rule set</p>
            {data && data[getActiveIndex]?.bag && <p>{data[getActiveIndex].bag?.name}</p>}
          </div>
          <div className="basis-1/4">
            <p className="ap-font-medium">Status</p>
            {data && data[getActiveIndex]?.status && <p>{AnalysisStatus[data[getActiveIndex]?.status || 2] as string}</p>}
          </div>
        </div>
      </div>
    </div>
  </LoaderWrapper>
}

export const MatchesAndUsers: FC<{ profile?: Profile, role?: Role, roleData: RoleListData }> = ({ profile, role, roleData }) => {
  const [matchIndex, setMatchIndex] = useState(0);
  const onTabChange = (i: number) => {
    setMatchIndex(i);
  }
  const getType = () => {
    if (profile) return profile?.isComposite ? ListType.CompositeProfile : ListType.Profile;
    if (role) return role?.isComposite ? ListType.CompositeRole : ListType.Role;
    return ListType.All;
  }

  const getMatchingData = (role: Role) => {
    return { roles: (role && roleData.roles.filter(f => f.roleId === role.roleId)) || [], compositeRoles: [], tests: [], compositeProfiles: [], profiles: [] }
  }
  const getView = (index: number) => {
    switch (index) {
      case 0:
        return <UserMatches
          type={getType()}
          typeId={profile?.profileId || role?.roleId || ""}
          matchingData={role && getMatchingData(role)} />
      case 1:
        return <UsersInline type={getType()} typeId={profile?.profileId || role?.roleId || ""} />
    }
  }
  return <div className="ap-wrapper-matches ap-accordion-open">
    <Tabs type="underline" activeIndex={matchIndex} style={{ padding: 8 }} onTabChange={onTabChange}>
      <Tab label="Matches" value="matches"></Tab>
      <Tab label="Users" value="users"></Tab>
      <Tab label="Test details" value="testdetails"></Tab>
    </Tabs>
    <div className="ap-wrapper-inline">
      {getView(matchIndex)}
    </div>
  </div>;
}

export const TechnicalRoles: FC<{ roles: Role[] }> = ({ roles }) => {
  const [search, setSearch] = useState("");
  const filteredRoles = useMemo(() => {
    return roles
      .filter(f => searchFilter(f, ["description", "name"], search))
      .flatMap(role => role.profiles?.map(profile => ({
        name: role.name,
        description: role.description,
        profile: profile.name,
        hasMatches: profile.hasMatches
      })) || []);
  }, [roles, search]);
  return (
    <Panel>
      <div className="flex items-center gap-4 mt-4">
        <div>
          <Search
            searchType={"secondary"}
            value={search}
            onChange={(value: string) => {
              setSearch(value);
            }}
            className="list-filter"
          />
        </div>
      </div>
      <ActiveFilters rows={filteredRoles.length} />
      <Table
        hasTitle
        condensed
        striped
        originalData={filteredRoles}>
        <Column field="name" sortKey="name">Role name</Column>
        <Column field="description" sortKey="description">Role description</Column>
        <Column field="profile" sortKey="profile">Profile</Column>
        <Column field="hasMatches" sortKey="hasMatches" renderCell={
          (row: { name: string, description: string, profile: string, hasMatches: boolean }, field: string) => {
            return row.hasMatches && <span className="Appkit4-icon icon-alert-fill access-risk"></span>
          }}>Access risks</Column>
      </Table>
    </Panel>
  )
}

export const TestDetails: FC<{ test: TestData }> = ({ test }) => {
  type TestRow = {
    function: string;
    description: string;
    transaction: string;
    transactionDescription: string
  };
  const [search, setSearch] = useState("");
  const [selectedFunction, setSelectedFunction] = useState<SelectValue>([]);

  const parseData = useCallback((testData?: RuleTest): TestRow[] => {
    if (!testData) return [];
    const result: TestRow[] = [];
    for (let f of testData?.functions || [])
      for (let t of f.transactions || [])
        result.push({ function: f.identifier, description: f.description, transaction: t.identifier, transactionDescription: t.description });
    return result;
  }, []);

  const { data, isPending } = FetchTestDetails(test.testId);
  const rowData = useMemo(() => parseData(data), [data, parseData]);

  return (isPending
    ? <Loader loadingType="circular" inline />
    : (
      <Panel>
        <h3>{data?.identifier}</h3>
        <p>{data?.functions?.map(m => <span className="ap-function-description">{m.description}</span>)}</p>
        <div className="flex items-center p-4 gap-4"
        >
          <div>
            <Search
              searchType={"secondary"}
              value={search}
              onChange={(value: string) => {
                setSearch(value);
              }}
              className="list-filter"
            />
          </div>
          <div>
            <Select
              placeholder="Function"
              data={Array.from(new Set(rowData.map(m => m.function))).map(m => ({ value: m, label: m }))
              }
              multiple={true}
              value={selectedFunction}
              suffixTemplate={item => {
                return (item.descValue > 0 && <span>{item.descValue} items</span>)
              }}
              onSelect={(v) => {
                setSelectedFunction(v);
              }}
              className="list-filter"
            />
          </div>
        </div>
        <Table
          originalData={
            rowData
              .filter(f => searchFilter(f, ["function", "description", "transaction", "transactionDescription"], search))
              .filter(f => selectFilter(f, "function", selectedFunction))}
          striped
          condensed
          hasTitle
        >
          <Column field="function" sortKey="function" renderCell={(row: TestRow) => (
            <>
              <b>{row.function}</b>
              <p>{row.description}</p>
            </>
          )}>Function</Column>
          <Column field="transaction" sortKey="description" renderCell={(row: TestRow) => (
            <>
              <b>{row.transaction}</b>
              <p>{row.transactionDescription}</p>
            </>
          )}>Transaction</Column>
        </Table>
      </Panel>
    )
  )
}
